export class PaginatedResponse<T> {
  data: T[] = [];
  page: number = 1;
  perPage: number = 25;
  total: number = 0;

  static removeElement<T>(oldData: PaginatedResponse<T>, item: T, key: keyof T) {
    const newData = new PaginatedResponse<T>();
    newData.data = oldData.data.filter(el => el[key] !== item[key]);
    newData.page = oldData.page;
    newData.perPage = oldData.perPage;
    newData.total = oldData.total - (oldData.data.length - newData.data.length);
    return newData;
  }
}

export type AdUnit = {
  feed: string
  adUnitId: string
  traffic_source?: string
}

export type ClassifiedImage = {
  etag: string
  url: string
  description: string
  category: string
  brand: string
  policy_compliance: string
  content_safety: string
  keywords: {keyword: string, adherent: boolean}[]
}

export type Environment = {
  id: number
  name: string
  bmId: string
  extendedCreditIds: string[]
  isInUse: boolean
}

export type ImagesArchiveV2 = {
  id: number
  keyword: string
  market: string
  hash: string
  url: string
  etag: string
}

export type KeywordToLaunchV2 = {
  id: number
  date: string // Date
  keyword: string
  market: string
  site: string
  adunit: string
  bid_amount: number
  bid_strategy: string
  ad_account: string
  budget: number
  custom_tag: string
  images_source_id: number
  ad_style: string
  status: 'new' | 'created' | 'errors'
  user: string
  environment: string
}

export type AiAdCopyV2 = {
  id: number
  created_at: Date
  updated_at: Date
  keyword: string
  language: string
  line_1: string
  line_2: string
}

export type KeywordV2 = {
  id: number
  keyword: string
  market: string
  language: string
  english_translation: string
  archive_date: Date
  vertical: string | null
  scraped_vertical: string | null
  scraped: boolean
  images: (Pick<ImagesArchiveV2, 'id' | 'hash' | 'url' | 'etag'> & {
    policy_compliance?: 'in policy' | 'not in policy' | null
    keyword_adherence?: 1 | 0 | null
  })[]
  ad_copies: Pick<AiAdCopyV2, 'id' | 'line_1' | 'line_2'>[]
}

export type KeywordDedupParseJson = {
  [key: string]: unknown
}

export type KeywordDedupListRow = {
  id: number
  keyword: string
  market: string
  vertical: string | null
  query_type: string | null
  recommended_campaign_keyword: string
  group_key: string | null
  grouped_keyword_count: number
  cluster_id: number | null
  decision: string
  created_at: string
}

export type KeywordDedupGroup = {
  canonical_keyword: string
  keywords: string[]
  keyword_count: number
}

export type KeywordDedupListResponse = {
  rows: KeywordDedupListRow[]
  count: number
  groups?: Record<string, KeywordDedupGroup>
}

export type KeywordDedupCluster = {
  id: number
  market: string
  canonical_keyword: string
  query_type: string | null
  base_entity: string | null
  cluster_confidence: number | null
  keyword_count: number
  status: string
  created_at: string | null
  updated_at: string | null
}

export type KeywordDedupCanonicalRow = {
  id: number | null
  keyword: string
  market: string
  vertical: string | null
  query_type: string | null
  recommended_campaign_keyword: string
  cluster_id: number | null
  decision: string
  created_at: string | null
}

export type KeywordDedupDetail = {
  id: number
  campaign_archive_id: number
  keyword: string
  market: string
  vertical: string | null
  archive_date: string | null
  normalized_surface: string | null
  query_type: string | null
  base_entity: string | null
  canonical_candidate: string | null
  cluster_id: number | null
  decision: string
  decision_reason: string | null
  confidence: number | null
  parse_json: KeywordDedupParseJson | null
  candidate_clusters_json: unknown
  error_message: string | null
  created_at: string
  updated_at: string
  recommended_campaign_keyword: string
  source_archive: {
    id: number
    keyword: string
    market: string
    archive_date: string
    vertical: string | null
  }
  cluster: {
    id: number
    market: string
    canonical_keyword: string
    query_type: string
    base_entity: string | null
    protected_attributes_json: unknown
    blocking_keys_json: unknown
    cluster_parse_json: unknown
    cluster_confidence: number | null
    keyword_count: number
    status: string
    created_at: string
    updated_at: string
  } | null
}

export type KeywordsToInsertItem = {
  keyword: string
  market: string
}

export type KeywordsToInsertBatchResponse = {
  batchId: string
  insertedCount: number
}

export type KeywordsToInsertBatchSummaryRow = {
  batchId: string
  status?: string
  created: string // Date
  totalKeywords: number
  keywordsWithOneImage: number
  keywordsWithVisymoArticles: number
  maskedKeywords: number
}

export type KeywordsToInsertBatchKeywordRow = {
  keyword: string
  market: string
  masking: number
  images: number
  visymoArticle: number
}

export type LaunchKeywordsV2Form = {
  accountId: string
  adStyles: Record<string, boolean>
  adsetNo: string
  bidAmount: string
  bidStrategy: string
  budget: string
  customTag: string
  duplicateFromArchive: boolean
  environment: string
  keywordId: string
  keywords: string
  markets: string[]
  sites: string[]
  startDate: string
}

export type Market = {
  id: number
  name: string
  code: string
  created_at: Date
  updated_at: Date
}

export type PostKeywordsV2RequestData = {
  accountId: string
  adStyle: string
  adsetNo: string
  adUnitId: string
  bidAmount: string
  bidStrategy: string
  budget: string
  customTag: string
  domain: string
  environment: string
  keyword: string
  keywordId: string
  market: string
  startDate: string
  user: string
}

export type Vertical = {
  id: number
  name: string
}

export type VideoArchive = {
  id: number
  created_at: string // Date
  updated_at: string // Date
  keyword: string
  market: string
  hash: string
  url: string
  etag: string
  snapshot_url: string
}

export type Website = {
  id: number
  domain: string
  subdomain: string
  site_tag: string
  supported_markets: string
  feed: string
  source_tag: string
  range_id: string
  ad_unit_id: string
  created_at: Date
  updated_at: Date
  adunits: AdUnit[]
}
